import { useState } from "react";
import { formatDate } from "date-fns";

import { InputField, SettingsList, SettingsListItem, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { Icon } from "../base/Icon";
import { useAuth } from "../../hooks/useAuth";
import { OrganizationMember } from "../../redux/reducers/organizationMemberSlice";
import { OrganizationInvite } from "../../redux/reducers/organizationInvitesSlice";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { ConfirmDialog } from "../ConfirmDialog";

type MembersCardProps = {
  members: OrganizationMember[];
  invites: OrganizationInvite[];
  onInviteUser: (email: string, role: "admin" | "member") => void;
  onCancelInvite: (inviteId: string) => void;
  onUpdateMemberRole: (memberId: string, role: "admin" | "member") => void;
  onRemoveMember: (memberId: string) => void;
  maxVisible?: number;
  className?: string;
};

export const MembersCard = ({
  members,
  invites,
  onInviteUser,
  onCancelInvite,
  onUpdateMemberRole,
  onRemoveMember,
  maxVisible,
  className,
}: MembersCardProps) => {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [emailError, setEmailError] = useState("");
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
  const [removingMember, setRemovingMember] = useState<OrganizationMember | null>(null);
  const [showCancelInviteDialog, setShowCancelInviteDialog] = useState(false);
  const [cancelingInvite, setCancelingInvite] = useState<OrganizationInvite | null>(null);
  const { user } = useAuth();
  const currentMember = members.find((member) => member.userId === user?.id);
  const isOrganizationAdmin = user?.isAdmin || currentMember?.role === "admin" || currentMember?.role === "owner";

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = { owner: "Owner", admin: "Admin", member: "Member" };
    return labels[role] || role;
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleInvite = () => {
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    onInviteUser(email, role);
    setShowInviteDialog(false);
    setEmail("");
    setRole("member");
    setEmailError("");
  };

  const handleRemoveMemberClick = (member: OrganizationMember) => {
    setRemovingMember(member);
    setShowRemoveMemberDialog(true);
  };

  const confirmRemoveMember = () => {
    if (removingMember) {
      onRemoveMember(removingMember.id);
      setShowRemoveMemberDialog(false);
      setRemovingMember(null);
    }
  };

  const handleCancelInviteClick = (invite: OrganizationInvite) => {
    setCancelingInvite(invite);
    setShowCancelInviteDialog(true);
  };

  const confirmCancelInvite = () => {
    if (cancelingInvite) {
      onCancelInvite(cancelingInvite.id);
      setShowCancelInviteDialog(false);
      setCancelingInvite(null);
    }
  };

  const pendingInvites = invites?.filter((i) => i.status === "pending") || invites || [];
  const totalCount = members.length + pendingInvites.length;

  return (
    <OrganizationSettingsTab
      title="Members"
      count={totalCount}
    >
      <SettingsList>
        {(() => {
          const allItems = [...members];
          const visibleMembers = maxVisible && !isExpanded ? allItems.slice(0, maxVisible) : allItems;
          const hiddenCount = maxVisible ? Math.max(0, allItems.length - maxVisible) : 0;

          return (
            <>
              {visibleMembers.map((member) => (
                <SettingsListItem
                  key={member.id}
                  title={member.name || "Unknown"}
                  subtitle={member.email}
                  icon={member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0).toUpperCase()}
                >
                  <View className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
                    {isOrganizationAdmin && member.userId !== user?.id && member.role !== "owner" && (
                      <>
                        <button
                          onClick={() => onUpdateMemberRole(member.id, member.role === "admin" ? "member" : "admin")}
                          className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 sm:flex-shrink-0"
                        >
                          {member.role === "admin" ? "Demote to Member" : "Promote to Admin"}
                        </button>
                        <button
                          onClick={() => handleRemoveMemberClick(member)}
                          className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 sm:flex-shrink-0"
                        >
                          Remove
                        </button>
                      </>
                    )}
                    <Text
                      variant="body"
                      className={`px-2 py-1 !mx-4 rounded-full text-xs font-medium ${member.role === "owner" ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400" : "bg-gray-100 dark:bg-gray-700"}`}
                    >
                      {getRoleLabel(member.role)}
                    </Text>
                  </View>
                </SettingsListItem>
              ))}
              {pendingInvites.map((invite) => (
                <SettingsListItem
                  key={invite.id}
                  title={invite.email}
                  subtitle={`Invited as ${invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}`}
                  icon="invite"
                >
                  <View className="flex items-center space-x-2">
                    <Text
                      variant="body"
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      Expires on {formatDate(invite.expiresAt, "MM/dd/yyyy")}
                    </Text>
                    {isOrganizationAdmin && (
                      <button
                        onClick={() => handleCancelInviteClick(invite)}
                        className="flex flex-row items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30"
                      >
                        Cancel Invite
                      </button>
                    )}
                  </View>
                </SettingsListItem>
              ))}
              {maxVisible && hiddenCount > 0 && !isExpanded && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="flex items-center justify-center gap-1 py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Show {hiddenCount} more
                </button>
              )}
              {maxVisible && isExpanded && members.length > (maxVisible || 0) && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="flex items-center justify-center gap-1 py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Show less
                </button>
              )}
              {isOrganizationAdmin && (
                <button
                  onClick={() => setShowInviteDialog(true)}
                  className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                >
                  <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
                    <Icon
                      name="add"
                      size={14}
                      className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
                    />
                  </View>
                  <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                    Invite member
                  </Text>
                </button>
              )}
            </>
          );
        })()}
      </SettingsList>

      {/* Invite User Dialog */}
      <Dialog
        isOpen={showInviteDialog}
        onClose={() => {
          setShowInviteDialog(false);
          setEmail("");
          setRole("member");
          setEmailError("");
        }}
        title="Invite User"
      >
        <View className="p-4 space-y-4">
          <InputField
            label="Email"
            placeholder="Enter email address"
            value={email}
            onChange={(value: string) => {
              setEmail(value);
              setEmailError("");
            }}
            error={emailError}
          />

          <View className="space-y-2">
            <Text
              variant="body"
              className="font-medium"
            >
              Role
            </Text>
            <View className="flex items-center space-x-4">
              <View className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="member"
                  name="role"
                  checked={role === "member"}
                  onChange={() => setRole("member")}
                  className="h-4 w-4 text-brand focus:ring-brand"
                />
                <label
                  htmlFor="member"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Member
                </label>
              </View>
              <View className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="admin"
                  name="role"
                  checked={role === "admin"}
                  onChange={() => setRole("admin")}
                  className="h-4 w-4 text-brand focus:ring-brand"
                />
                <label
                  htmlFor="admin"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Admin
                </label>
              </View>
            </View>
          </View>

          <View className="flex gap-3 pt-2">
            <button
              onClick={handleInvite}
              disabled={!email}
              className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon
                name="send"
                size={14}
              />
              Send Invite
            </button>
            <button
              onClick={() => {
                setShowInviteDialog(false);
                setEmail("");
                setRole("member");
                setEmailError("");
              }}
              className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </View>
        </View>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveMemberDialog}
        onClose={() => {
          setShowRemoveMemberDialog(false);
          setRemovingMember(null);
        }}
        onConfirm={confirmRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${removingMember?.name || removingMember?.email} from the organization?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The member will lose access to the organization immediately.
        </Text>
      </ConfirmDialog>

      {/* Cancel Invite Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelInviteDialog}
        onClose={() => {
          setShowCancelInviteDialog(false);
          setCancelingInvite(null);
        }}
        onConfirm={confirmCancelInvite}
        title="Cancel Invitation"
        message={`Are you sure you want to cancel the invitation for ${cancelingInvite?.email}?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          This action cannot be undone. The invitation will be cancelled and the link will no longer work.
        </Text>
      </ConfirmDialog>
    </OrganizationSettingsTab>
  );
};
